import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { hasNext } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type { LookerFolder } from 'lumen/models';

interface MarketCustomReportingFormFieldsBreadcrumbsSignature {
  Args: {
    folder: LookerFolder;
  };
}

export default class MarketCustomReportingFormFieldsBreadcrumbs extends Component<MarketCustomReportingFormFieldsBreadcrumbsSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  @cached get breadcrumbs() {
    const { folder } = this.args;
    const breadcrumbs = this.getFolderBreadcrumbs(folder, []).reverse();

    if (breadcrumbs.length <= 3) {
      return breadcrumbs;
    }

    return [
      ...breadcrumbs.slice(0, 2),
      '...',
      ...breadcrumbs.slice(breadcrumbs.length - 1),
    ];
  }

  private getFolderBreadcrumbs(
    folder: LookerFolder,
    breadcrumbs: string[] = [],
  ): string[] {
    if (folder.isPersonal) {
      breadcrumbs.push(this.intl.t('custom_reporting.my_dashboards'));

      return breadcrumbs;
    }

    if (folder.isEmbedSharedRoot) {
      if ([...folder.folders].length > 1) {
        breadcrumbs.push(this.intl.t('custom_reporting.portfolios'));
      } else {
        breadcrumbs.push(this.intl.t('custom_reporting.portfolio'));
      }

      return breadcrumbs;
    }

    if (folder.belongsTo('folder').value()) {
      breadcrumbs.push(folder.name);

      return this.getFolderBreadcrumbs(folder.folder, breadcrumbs);
    }

    return breadcrumbs;
  }

  <template>
    <div class="location mb-400 flex flex-col gap-100">
      <p class="text-200--bold title">{{t "custom_reporting.form.location"}}</p>
      <div
        class="breadcrumbs flex flex-row items-center gap-100"
        data-test-id="folder-breadcrumbs"
      >
        {{#let (eq this.breadcrumbs.length 2) as |hasTwoBreadcrumbs|}}
          {{#each this.breadcrumbs as |folderName|}}
            <span
              class="text-300--regular truncate
                {{if hasTwoBreadcrumbs 'two-level'}}"
            >
              {{folderName}}
            </span>

            {{#if (hasNext folderName this.breadcrumbs)}}
              <PrismIcon @name="chevron-right" @size="small" />
            {{/if}}
          {{/each}}
        {{/let}}
      </div>
    </div>
  </template>
}
